import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import NetworkService from "../../../NetworkService";
import { getVisibleFavoriteAddresses } from "../favoriteAddresses";
import { FavoriteAddress, GeocodeSuggestion, normalizeGeocodeResults } from "../types";
import styles from "./orderForm.module.scss";

type AddressAutocompleteInputProps = {
  prefix: string;
  value: string;
  placeholder: string;
  loadingLabel: string;
  emptyLabel: string;
  addOnMapLabel: string;
  favoriteSectionLabel: string;
  searchResultsSectionLabel: string;
  favoriteAddresses?: FavoriteAddress[];
  onChange: (value: string) => void;
  onSelect: (suggestion: GeocodeSuggestion) => void;
  onCoordsClear: () => void;
  onAddOnMap: () => void;
  disabled?: boolean;
  showClear?: boolean;
  clearAriaLabel?: string;
};

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  prefix,
  value,
  placeholder,
  loadingLabel,
  emptyLabel,
  addOnMapLabel,
  favoriteSectionLabel,
  searchResultsSectionLabel,
  favoriteAddresses = [],
  onChange,
  onSelect,
  onCoordsClear,
  onAddOnMap,
  disabled = false,
  showClear = false,
  clearAriaLabel = "Clear address",
}) => {
  const listIdRef = useRef(`address-list-${Math.random().toString(36).slice(2, 9)}`);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [apiSuggestions, setApiSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const debouncedSetQuery = useCallback(
    debounce((query: string) => setDebouncedQuery(query.trim()), 300),
    []
  );

  useEffect(() => {
    debouncedSetQuery(value);
    return () => debouncedSetQuery.cancel();
  }, [value, debouncedSetQuery]);

  const favoriteQuery = value.trim().length === 0 ? "" : debouncedQuery;

  const favoriteMatches = useMemo(
    () => getVisibleFavoriteAddresses(favoriteAddresses, favoriteQuery),
    [favoriteAddresses, favoriteQuery]
  );

  const searchSuggestions = useMemo(() => {
    if (favoriteMatches.length === 0) return apiSuggestions;

    const favoriteAddressKeys = new Set(
      favoriteMatches.map((item) => item.address_name.trim().toLowerCase())
    );

    return apiSuggestions.filter(
      (item) => !favoriteAddressKeys.has(item.formattedAddress.trim().toLowerCase())
    );
  }, [apiSuggestions, favoriteMatches]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedQuery.length < 2) {
        setApiSuggestions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const res = await NetworkService.geocode(debouncedQuery);
        setApiSuggestions(normalizeGeocodeResults(res));
      } catch {
        setApiSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (next: string) => {
    if (disabled) return;
    onChange(next);
    setIsOpen(true);
    if (next.trim().length < 2) {
      setApiSuggestions([]);
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion: GeocodeSuggestion) => {
    onSelect(suggestion);
    setApiSuggestions([]);
    setIsOpen(false);
  };

  const handleClear = () => {
    if (disabled) return;
    onChange("");
    onCoordsClear();
    setApiSuggestions([]);
    setIsOpen(false);
    setIsLoading(false);
  };

  const canClear = showClear && !disabled && value.trim().length > 0;
  const hasQuery = debouncedQuery.length >= 2;
  const hasFavoriteResults = favoriteMatches.length > 0;
  const hasSearchResults = searchSuggestions.length > 0;
  const showEmptyState = hasQuery && !isLoading && !hasFavoriteResults && !hasSearchResults;

  return (
    <div className={styles.acWrap} ref={wrapRef}>
      <label className={`${styles.acField} ${disabled ? styles.acFieldDisabled : ""}`}>
        <span className={styles.acPrefix}>{prefix}</span>
        <input
          type="text"
          value={value}
          className={canClear ? styles.acInputWithClear : undefined}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (!disabled) setIsOpen(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          required
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listIdRef.current}
          aria-autocomplete="list"
        />
        {canClear && (
          <button
            type="button"
            className={styles.acClearBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            aria-label={clearAriaLabel}
          >
            ×
          </button>
        )}
      </label>

      {isOpen && (
        <ul id={listIdRef.current} className={styles.acDropdown} role="listbox">
          {hasFavoriteResults && (
            <>
              <li className={styles.acSectionHeader} aria-hidden>
                {favoriteSectionLabel}
              </li>
              {favoriteMatches.map((item) => (
                <li key={`favorite-${item.id}`} role="option">
                  <button
                    type="button"
                    className={styles.acOption}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() =>
                      handleSelect({
                        formattedAddress: item.address_name,
                        lat: item.lat,
                        lng: item.lng,
                      })
                    }
                  >
                    {item.address_name}
                  </button>
                </li>
              ))}
            </>
          )}

          {hasSearchResults && (
            <>
              {hasFavoriteResults && (
                <li className={styles.acSectionHeader} aria-hidden>
                  {searchResultsSectionLabel}
                </li>
              )}
              {searchSuggestions.map((item, index) => (
                <li key={`search-${item.formattedAddress}-${index}`} role="option">
                  <button
                    type="button"
                    className={styles.acOption}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(item)}
                  >
                    {item.formattedAddress}
                  </button>
                </li>
              ))}
            </>
          )}

          {isLoading && (
            <li className={styles.acStatus} role="option">
              {loadingLabel}
            </li>
          )}

          {showEmptyState && (
            <li className={styles.acStatus} role="option">
              {emptyLabel}
            </li>
          )}

          <li role="option">
            <button
              type="button"
              className={styles.acMapAction}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setApiSuggestions([]);
                setIsOpen(false);
                onAddOnMap();
              }}
            >
              {addOnMapLabel}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default AddressAutocompleteInput;
